import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid, Icon, Segment } from "semantic-ui-react";
import {
  fetchJobsEntriesForDashboard,
  updateJobDashboardDatesFilter,
  updateJobDashboardHourlyRateFilter,
  updateJobDashboardOnCallDutyFilter,
  updateJobDashboardSystemsFilter,
  updateJobDashboardTimeOfDayFilter,
  updateJobDashboardWorkfields,
  updateJobsDashboardFilters,
  updateJobsDashboardLocation,
  updateJobsDashboardWorkfieldGroup,
  updateJobsDashboardWorkrole,
} from "../../../redux/Jobs/jobsActions";
import useTheme from "../../../hooks/useTheme";
import { AppContainer } from "../../UI/Container";
import { AppColumn, AppRow } from "../../UI/Layouts";
import { useResponsive } from "../../UI/ResponsiveContext";
import { SizedBox } from "../../UI/Sizes";
import AppText from "../../UI/Texts/AppTexts";
import JobCard from "./Components/JobCard";
import "./JobListings.css";
import {
  AppLinkedText,
  AppOutlineButton,
  AppPrimaryButton,
} from "../../UI/CustomButtons";
import { REGISTRATION_PATH } from "../../../utils/Constants/Paths";
import AppFooter from "../../Footers/AppFooter";
import CircularLoader from "../../UI/LoadingIndicators/CircularLoadingIndicators";
import { t } from "i18next";
import { LocalStorageKeys } from "../../../utils/Constants/localStorageKeys";
import {
  convertHomePreferencesToJobFilters,
  getJobFiltersFromQueryParams,
} from "../../../utils/helperFunctions";

const JobsListing = ({
  fetchJobsEntriesForDashboard,
  updateJobsDashboardFilters,
  updateJobsDashboardWorkfieldGroup,
  updateJobsDashboardLocation,
  updateJobsDashboardWorkrole,
  updateJobDashboardDatesFilter,
  updateJobDashboardHourlyRateFilter,
  updateJobDashboardSystemsFilter,
  updateJobDashboardTimeOfDayFilter,
  updateJobDashboardOnCallDutyFilter,
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const location = useLocation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const jobsState = useSelector((state) => state.jobs) ?? {};
  const coreState = useSelector((state) => state.core) ?? {};

  const queryParams = new URLSearchParams(location.search);
  const forceRefresh = queryParams.get("force-refresh") === "1";

  const constants = coreState?.constants ?? {};

  const fetchingInitialJobsRef = useRef(null);

  const jobsDashboardFilters = jobsState?.jobsDashboardFilters;
  const hasMore = jobsState?.dashboardJobsHasMore ?? false;

  const jobs = jobsState.dashboardJobs ?? [];

  const isLoadingJobs = jobsState?.jobsDashboardLoading ?? true;

  const isSSR =
    typeof window === "undefined" ||
    (typeof window !== "undefined" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1");

  const locationFilters = jobsDashboardFilters?.locations ?? [];

  let jobsLocation = locationFilters[0];

  let constantJobLocations = constants?.locations ?? [];

  if (
    jobsLocation &&
    jobsLocation !== "all_sweden" &&
    constantJobLocations.includes(jobsLocation)
  ) {
    jobsLocation = `${jobsLocation}`;
  } else {
    jobsLocation = "Sverige";
  }

  const initialFetchDone = jobsState?.initialJobFetchDone ?? false;

  const [isFetchingInitialJobs, setIsFetchingInitialJobs] = useState(
    !(jobsState?.initialJobFetchDone ?? false)
  );

  const getDataFromQueryParams = () => {
    const isSSR = typeof window === "undefined";
    const search = isSSR ? location.search : window.location.search;

    return getJobFiltersFromQueryParams(constants, search);
  };

  useEffect(() => {
    if (jobsDashboardFilters && (!initialFetchDone || forceRefresh) ) {
      fetchJobs();
    }
  }, [jobsDashboardFilters, initialFetchDone,forceRefresh]);

  useEffect(
    () => {
      setJobFiltersToQueryParams();
    },
    (() => {
      if (!isSSR) {
        return [location.search, coreState?.constants];
      }
      return [location.search];
    })()
  );

  const fetchJobs = async () => {
    setIsFetchingInitialJobs(true);

    if (fetchingInitialJobsRef.current) {
      clearTimeout(fetchingInitialJobsRef.current);
    }

    fetchingInitialJobsRef.current = setTimeout(async () => {
      await fetchJobsEntriesForDashboard(false, jobsDashboardFilters);
      setIsFetchingInitialJobs(false);

      fetchingInitialJobsRef.current = null;
    }, 300);
  };

  const setJobFiltersToQueryParams = async () => {
    const localHomePreferences = JSON.parse(
      localStorage.getItem(LocalStorageKeys.HOME_PREFERENCES)
    );

    const homeQueryParamsData = getDataFromQueryParams();

    const checkForceRefresh = homeQueryParamsData?.forceRefresh ?? false;

    const hasDataQueryParams = Object.values(homeQueryParamsData).some(
      (value) => (Array.isArray(value) ? value.length > 0 : value !== null)
    );

    if (hasDataQueryParams && !checkForceRefresh) {
      dispatch(updateJobsDashboardFilters(homeQueryParamsData));
    } else {
      const newJobFilters = convertHomePreferencesToJobFilters(
        constants,
        localHomePreferences,
        jobsDashboardFilters
      );

      dispatch(updateJobsDashboardFilters(newJobFilters));
    }
  };

  function onLocationUpdate(e) {
    dispatch(updateJobsDashboardLocation([e]));
  }

  function onWorkroleUpdate(e) {
    dispatch(updateJobsDashboardWorkrole([e]));
  }

  function onWorkfieldUpdate(group, workfields) {
    dispatch(updateJobsDashboardWorkfieldGroup(group, workfields));
  }

  function onDatesUpdate(fromDate, toDate) {
    dispatch(updateJobDashboardDatesFilter(fromDate, toDate));
  }

  function onSystemRequirementUpdate(systems) {
    dispatch(updateJobDashboardSystemsFilter([...systems]));
  }

  function onTimeOfDateUpdate(timeOfDay) {
    dispatch(updateJobDashboardTimeOfDayFilter([...timeOfDay]));
  }

  function onHourlyRateUpdate(min, max) {
    dispatch(updateJobDashboardHourlyRateFilter(min, max));
  }

  function onCallDutyChangeUpdate(onCallDuty) {
    dispatch(updateJobDashboardOnCallDutyFilter(onCallDuty));
  }

  function clearFilter() {
    onLocationUpdate(null);
    onWorkroleUpdate(null);
    onWorkfieldUpdate(null, []);
    onDatesUpdate(null, null);
    onSystemRequirementUpdate([]);
    onTimeOfDateUpdate([]);
    onHourlyRateUpdate(null, null);
    onCallDutyChangeUpdate(null);

    localStorage.removeItem(LocalStorageKeys.HOME_PREFERENCES);
    fetchJobsEntriesForDashboard();

    navigate(location.pathname, { replace: true });
  }

  const filters = getDataFromQueryParams();

  const hasFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== null
  );

  return (
    <div>
      {(() => {
        const hasJobs = jobs.length > 0;

        if (isFetchingInitialJobs && !isSSR) {
          return (
            <Segment
              basic
              loading
              placeholder
              style={{
                height: "80vh",
                width: "100%",
              }}
            />
          );
        } else if (hasJobs) {
          return (
            <AppContainer
              style={{
                width: "100%",
                minHeight: "80vh",
              }}
            >
              <SizedBox height={isMobile && theme.spacing.contentElementPadding} />
              <AppRow crossAxisAlignment={"center"}>
                <Icon
                  name={hasFilters ? "lightbulb" : "map marker alternate"}
                  style={{
                    color: theme.primaryColor,
                    fontSize: "1rem",
                    marginBottom: "0.5rem",
                  }}
                />

                <AppText.Caption use={"h2"}>
                  {hasFilters
                    ? t("home.suggested_jobs.found", {
                        count: jobs.length,
                      })
                    : t("home.available_care_in", {
                        location: jobsLocation,
                      })}
                </AppText.Caption>
              </AppRow>
              <SizedBox height={theme.spacing.contentElementPadding} />
              <Grid stackable columns={2}>
                {jobs.length > 0 &&
                  jobs.map((job, index) => (
                    <Grid.Column
                      key={`${job.jobId}_${index}`}
                      style={{
                        padding: theme.spacing.elementPadding,
                      }}
                    >
                      <JobCard jobData={job} />
                    </Grid.Column>
                  ))}
              </Grid>
              <SizedBox height={theme.spacing.contentPaddingBig} />
              {hasMore && (
                <AppColumn crossAxisAlignment={"center"}>
                  <AppText.BodyText
                    center
                    margins={{
                      marginBottom: "0px",
                    }}
                  >
                    {t("home.continue_exploring_jons")}
                  </AppText.BodyText>
                  <SizedBox height={theme.spacing.elementPadding} />
                  {(() => {
                    if (isLoadingJobs) return <CircularLoader />;

                    return (
                      <AppPrimaryButton
                        size="big"
                        style={{
                          borderRadius: theme.radius.small,
                        }}
                        onClick={() => {
                          fetchJobsEntriesForDashboard(true);
                        }}
                      >
                        {t("home.button.show_more")}
                      </AppPrimaryButton>
                    );
                  })()}
                </AppColumn>
              )}
            </AppContainer>
          );
        } else if (!isLoadingJobs && jobs.length <= 0) {
          return <NoJobsFound onClear={clearFilter} />;
        } else {
          return (
            <Segment
              basic
              loading
              placeholder
              style={{
                height: "80vh",
                width: "100%",
              }}
            />
          );
        }
      })()}

      <SizedBox height={theme.spacing.contentPaddingBig} />
      <AppFooter forceShow={true} />
    </div>
  );
};

export default connect(null, {
  fetchJobsEntriesForDashboard,
  updateJobsDashboardFilters,
  updateJobDashboardWorkfields,
  updateJobsDashboardWorkfieldGroup,
  updateJobsDashboardLocation,
  updateJobsDashboardWorkrole,
  updateJobDashboardDatesFilter,
  updateJobDashboardHourlyRateFilter,
  updateJobDashboardSystemsFilter,
  updateJobDashboardTimeOfDayFilter,
  updateJobDashboardOnCallDutyFilter,
})(JobsListing);

const NoJobsFound = ({ onClear }) => {
  const theme = useTheme();
  const navigation = useNavigate();

  const currentUser = useSelector((state) => state.auth.currentUser);
  const isAuthenticated = currentUser != null;

  return (
    <AppColumn
      mainAxisAlignment={"center"}
      crossAxisAlignment={"center"}
      style={{
        padding: theme.spacing.contentElementPadding,
        height: "60vh",
        width: "100%",
      }}
    >
      {isAuthenticated ? (
        <AppColumn crossAxisAlignment={"center"}>
          <Icon
            name="frown outline"
            size="huge"
            style={{ width: "40%", color: theme.colors.grey }}
          />
          <SizedBox height={theme.spacing.contentElementPadding} />
          <AppText.BodyText center color={theme.colors.grey}>
            {t("home.nojobs.title")}
          </AppText.BodyText>
          <SizedBox height={theme.spacing.elementPadding} />

          <AppPrimaryButton
            outline={true}
            size="large"
            onClick={() => {
              onClear();
            }}
            style={{
              borderRadius: theme.radius.small,
            }}
          >
            {t("home.nojobs.button.search_again")}
          </AppPrimaryButton>
        </AppColumn>
      ) : (
        <AppColumn crossAxisAlignment={"center"}>
          <AppText.BodyText center>
            {t("home.nojobs.description", { appName: theme.appName })}
          </AppText.BodyText>
          <SizedBox height={theme.spacing.contentElementPadding} />
          <AppOutlineButton
            outline={true}
            size="large"
            onClick={() => {
              navigation(REGISTRATION_PATH);
            }}
            style={{
              borderRadius: theme.radius.small,
            }}
          >
            {t("okay")}
          </AppOutlineButton>
          <SizedBox height={theme.spacing.elementPadding} />
          <AppLinkedText
            text={t("home.nojobs.button.clear_search_again")}
            underline
            onClick={() => {
              onClear();
            }}
            color={theme.colors.grey}
          />
        </AppColumn>
      )}
    </AppColumn>
  );
};
